<template>
  <div class="container mx-auto p-4">
    <page-header title="Webhook Events" subtitle="Review incoming events and replay them when needed." />

    <div class="flex justify-between mb-4">
      <input-search v-model.trim="search" class="max-w-lg grow" />
      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :columns="columns"
      :data-handler="getWebhookEvents"
      row-class="hover:bg-slate-50"
      :per-page="15"
      @error="onError"
      @replay="onReplay"
    />
  </div>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import { useToast } from 'vue-toastification';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import PageHeader from '../components/PageHeader.vue';
import Grid from '../components/grid/Grid.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import WebhookListActions from '../components/WebhookListActions.vue';
import { apiDomain } from '../composables/useConstants';
import { formatDateTime } from '../utils/formatters';
import { elipsis } from '../utils/elipsis';

const toast = useToast();
const gridRef = ref(null);

const search = useRouteQuery('search', null);

function reloadGrid(updateQuery = {}) {
  gridRef.value?.reload(updateQuery);
}

watchDebounced(
  search,
  () => reloadGrid({ offset: 0 }),
  { debounce: 500, maxWait: 1000 }
);

function getProcessingStateClass(state) {
  const base = 'text-center rounded-lg text-lg scale-70';
  if (state === 'USER_NOT_FOUND') return `${base} bg-red-100 !text-red-800`;
  if (state === 'PROCESSED') return `${base} bg-green-100 !text-green-800`;
  if (state === 'PENDING') return `${base} bg-yellow-100 !text-yellow-800`;
  return `${base} bg-gray-100 !text-gray-800`;
}

async function getWebhookEvents({ limit, offset, sort, direction }) {
  const params = new URLSearchParams({
    limit,
    offset,
    sort: sort || '',
    direction: direction || '',
    search: search.value ?? ''
  });
  const { data } = await useFetch(`${apiDomain}/api/stripe/admin/webhook-events?${params}`).get().json();
  return {
    total: data.value?.pagination?.total ?? 0,
    rows: data.value?.data ?? []
  };
}

function onError(message) {
  toast.error(message);
}

async function onReplay(row) {
  try {
    await useFetch(`${apiDomain}/api/stripe/admin/webhook-events/${row.id}/replay`).post().json();
    toast.success('Webhook replayed successfully!');
  } catch (error) {
    console.error('Error replaying webhook:', error);
    toast.error('Failed to replay webhook.');
  }
}

const columns = [
  {
    title: 'Event ID',
    value: row => elipsis(row.id, 24),
    class: 'text-sm font-mono text-xs'
  },
  {
    title: 'User ID',
    value: row => row.userId ? elipsis(row.userId) : '',
    class: 'text-sm text-slate-600'
  },
  {
    title: 'Customer ID',
    value: row => row.stripeCustomerId ? elipsis(row.stripeCustomerId) : '',
    class: 'text-sm text-slate-600'
  },
  {
    title: 'Event Type',
    prop: 'eventType'
  },
  {
    title: 'State',
    prop: 'processingState',
    class: row => getProcessingStateClass(row.processingState),
    titleClass: '!text-center'
  },
  {
    title: 'Details',
    prop: 'details',
    class: 'text-sm text-slate-600 max-w-[200px] truncate'
  },
  {
    title: 'Created At',
    value: row => formatDateTime(row.createdAt),
    class: 'whitespace-nowrap text-xs'
  },
  {
    title: '',
    component: markRaw(WebhookListActions),
    class: 'whitespace-nowrap'
  }
];
</script>
